// 0.7 — the entry of the HOSTED web app (app.avrastro.com, served by the AVRASTRO platform itself). There the CRM is an
// account service: the first screen offers only sign-in, account creation and password reset — not the local-only
// quick starts of the desktop/offline editions (guided local setup, import, demo data, start without data), which stay
// unchanged there (modules/onboarding.tsx). After sign-in the workspace is prepared from the account profile and the
// app opens; signing out (or a session that ends) brings this screen back. Data already on the device is never touched.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { REGIONS, regionPack, saveSetting } from '@vero/domain';
import type { Workspace } from '@vero/store';
import { createI18n } from '@vero/i18n';
import { passwordProblem, PlatformError, type PlatformInfo } from '@vero/platform';
import { VeroLogo } from '../core/brand.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';
import { accountService, sameOriginPlatform, type AccountUiState } from '../core/account-service.ts';
import { EDITION } from '../core/edition.ts';
import { getPasskey, passkeysSupported } from '../core/passkey.ts';

type Ev = { target: HTMLInputElement };
type SelEv = { target: HTMLSelectElement };
type Lang = 'el' | 'en';
type Tab = 'signin' | 'register' | 'reset';
type T = (k: string, p?: Record<string, string | number>) => string;

/** the web app served by the platform (app.avrastro.com): an account is the way in */
export const hostedEntry = (): boolean => EDITION === 'standard' && !!sameOriginPlatform();

/** where the legal texts live (generated from the website content by tools/build-legal.mjs) */
export const legalUrl = (page: 'terms' | 'privacy' | 'refund', lang: string, origin = ''): string => `${origin}/legal/${lang === 'en' ? 'en/' : ''}${page}/`;

/** e-mail links arrive as …/#/account?reset=…, ?invite=…, ?verify=… */
function linkParams(): { reset: string; invite: string } {
  const q = new URLSearchParams((typeof location !== 'undefined' ? location.hash : '').split('?')[1] ?? '');
  return { reset: q.get('reset') ?? '', invite: q.get('invite') ?? '' };
}
/** one-time codes are read once and removed from the address bar and history */
function dropFromUrl(...keys: string[]) {
  const [path, q = ''] = location.hash.slice(1).split('?');
  const qs = new URLSearchParams(q);
  if (!keys.some((k) => qs.has(k))) return;
  for (const k of keys) qs.delete(k);
  const rest = qs.toString();
  history.replaceState(null, '', `${location.pathname}${location.search}#${path || '/now'}${rest ? `?${rest}` : ''}`);
}

/** the account state for the boot root — only followed where it decides what is shown (the hosted web app), so the
 *  other editions render exactly as before */
export function useAccountState(enabled = true): AccountUiState | null {
  const [s, setS] = useState<AccountUiState | null>(() => (enabled ? accountService()?.snapshot() ?? null : null));
  useEffect(() => (enabled ? accountService()?.subscribe(setS) : undefined), [enabled]);
  return s;
}

function errorText(t: T, e: unknown): string {
  const code = e instanceof PlatformError ? e.message : e instanceof TypeError ? 'network' : String((e as Error)?.message ?? e);
  const params = { workspace: accountService()?.localWorkspace ?? '—' };
  for (const k of [`gate.err.${code}`, `account.err.${code}`]) { const x = t(k, params); if (x !== k) return x; }
  return t('account.err.generic', { code });
}

// ---------------------------------------------------------------------------------------------- the gate
export function Gate({ acct, onLang }: { acct: AccountUiState | null; onLang: (l: Lang) => void }) {
  const origin = sameOriginPlatform() ?? location.origin;
  const links = useMemo(linkParams, []);
  const [lang, setLangState] = useState<Lang>(() => {
    const p = acct?.profile?.language;
    return p === 'en' || p === 'el' ? p : navigator.language?.toLowerCase().startsWith('el') ? 'el' : (navigator.languages ?? []).some((l) => l.toLowerCase().startsWith('el')) ? 'el' : 'en';
  });
  const setLang = (l: Lang) => setLangState(l);
  useEffect(() => { onLang(lang); document.documentElement.lang = lang; }, [lang]);
  const i18n = useMemo(() => createI18n({ locale: lang }), [lang]);
  const t: T = (k, p) => i18n.t(k, p);
  const [tab, setTab] = useState<Tab>(links.reset ? 'reset' : links.invite ? 'register' : 'signin');
  const [info, setInfo] = useState<PlatformInfo | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState(acct?.email ?? '');
  useEffect(() => { document.title = `${t('gate.title')} · AVRASTRO`; }, [lang]);
  useEffect(() => {
    let on = true;
    const svc = accountService();
    if (!svc) return;
    svc.info(origin).then((i) => { if (on) { setInfo(i); setOffline(false); } }).catch(() => { if (on) setOffline(true); });
    void svc.website(origin).then((w) => { if (on) setSite(w); });
    return () => { on = false; };
  }, []);
  // the codes from e-mail links are now in the form: take them out of the address bar
  useEffect(() => { dropFromUrl('reset', 'invite'); }, []);

  if (!accountService()) return <div className="boot"><div className="card card-pad gate-msg" role="alert">{t('gate.unavailable')}</div></div>;
  const host = site ? (() => { try { return new URL(site).host; } catch { return site; } })() : null;
  const go = (x: Tab) => { setNotice(null); setTab(x); };

  return (
    <div className="gate" data-testid="gate">
      <div className="gate-card">
        <section className="gate-brand" aria-labelledby="gate-title">
          <VeroLogo width={132} className="gate-logo" />
          <h1 id="gate-title" className="gate-title">{t('gate.title')}</h1>
          <p className="gate-sub">{t('gate.sub')}</p>
          <ul className="gate-facts">
            <li><Icon name="cloud" size={17} /><span>{t('gate.fact.sync')}</span></li>
            <li><Icon name="shield" size={17} /><span>{t('gate.fact.backup')}</span></li>
            <li><Icon name="people" size={17} /><span>{t('gate.fact.team')}</span></li>
          </ul>
          <nav className="gate-legal" aria-label={t('gate.legal')}>
            <a href={legalUrl('terms', lang)} target="_blank" rel="noopener" data-testid="gate-terms-link">{t('gate.terms_page')}</a>
            <a href={legalUrl('privacy', lang)} target="_blank" rel="noopener">{t('gate.privacy_page')}</a>
          </nav>
        </section>

        <section className="gate-form" aria-label={t(`account.tab.${tab === 'reset' ? 'reset' : tab}`)}>
          <div className="gate-top">
            {tab !== 'reset' ? <div className="segmented gate-tabs" role="tablist">
              {(['signin', 'register'] as const).map((x) => <button key={x} type="button" role="tab" aria-selected={tab === x} aria-pressed={tab === x} onClick={() => go(x)} data-testid={`gate-tab-${x}`}>{t(`account.tab.${x}`)}</button>)}
            </div> : <h2 className="gate-h2">{t('gate.reset_title')}</h2>}
            <div className="segmented gate-lang" role="group" aria-label={t('settings.language')}>
              {(['el', 'en'] as const).map((l) => <button key={l} type="button" aria-pressed={lang === l} onClick={() => setLang(l)} lang={l} data-testid={`gate-lang-${l}`}>{l === 'el' ? 'ΕΛ' : 'EN'}</button>)}
            </div>
          </div>

          {acct?.revoked && tab === 'signin' && <p className="callout warn small" data-testid="gate-session-ended">{t('gate.session_ended')}</p>}
          {acct?.lastError === 'website_session_ended' && tab === 'signin' && <p className="callout warn small">{t('account.err.website_session_ended')}</p>}
          {offline && <p className="callout warn small" role="alert" data-testid="gate-offline">{t('gate.err.network')}</p>}
          {notice && <p className="callout ok small" role="status" data-testid="gate-notice">{notice}</p>}

          {tab === 'signin' && <SignIn t={t} origin={origin} email={email} setEmail={setEmail} onForgot={() => go('reset')} site={site} host={host} />}
          {tab === 'register' && <Register t={t} lang={lang} origin={origin} info={info} invite={links.invite} />}
          {tab === 'reset' && <Reset t={t} origin={origin} token0={links.reset} email={email} setEmail={setEmail} onDone={() => { setTab('signin'); setNotice(t('gate.reset_done')); }} onBack={() => go('signin')} />}

          {tab === 'signin' && <p className="gate-switch small">{t('gate.no_account')} <button type="button" className="linklike" onClick={() => go('register')} data-testid="gate-to-register">{t('account.create')}</button></p>}
          {tab === 'register' && <p className="gate-switch small">{t('gate.have_account')} <button type="button" className="linklike" onClick={() => go('signin')}>{t('account.sign_in')}</button></p>}
          <nav className="gate-legal gate-legal-foot" aria-label={t('gate.legal')}>
            <a href={legalUrl('terms', lang)} target="_blank" rel="noopener">{t('gate.terms_page')}</a>
            <a href={legalUrl('privacy', lang)} target="_blank" rel="noopener">{t('gate.privacy_page')}</a>
          </nav>
        </section>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------- pieces
function PasswordField({ t, id, label, value, onChange, autoComplete, testId, extra }: { t: T; id: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string; testId: string; extra?: ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div className="field gate-field">
      <div className="gate-label-row"><label className="label" htmlFor={id}>{label}</label>{extra}</div>
      <div className="gate-pw">
        <input id={id} className="input" type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e: Ev) => onChange(e.target.value)} data-testid={testId} spellCheck={false} autoCapitalize="none" />
        <button type="button" className="gate-pw-toggle" aria-pressed={show} aria-controls={id} aria-label={t(show ? 'gate.hide_password' : 'gate.show_password')} onClick={() => setShow(!show)}>{t(show ? 'gate.hide' : 'gate.show')}</button>
      </div>
    </div>
  );
}
function Text({ id, label, value, onChange, type = 'text', autoComplete, testId, hint, optional, t }: { id: string; label: string; value: string; onChange: (v: string) => void; type?: string; autoComplete?: string; testId?: string; hint?: string; optional?: boolean; t: T }) {
  return (
    <div className="field gate-field">
      <label className="label" htmlFor={id}>{label}{optional && <span className="gate-optional"> {t('gate.optional')}</span>}</label>
      <input id={id} className="input" type={type} autoComplete={autoComplete} value={value} onChange={(e: Ev) => onChange(e.target.value)} data-testid={testId} {...(type === 'email' ? { spellCheck: false, autoCapitalize: 'none', inputMode: 'email' as const } : {})} />
      {hint && <small className="tiny muted">{hint}</small>}
    </div>
  );
}
function PasswordHint({ t, pw, email }: { t: T; pw: string; email: string }) {
  if (!pw) return <small className="tiny muted">{t('account.pw_rule')}</small>;
  const p = passwordProblem(pw, email);
  return <small className="tiny" style={{ color: p ? 'var(--high)' : 'var(--ok)' }} data-testid="pw-hint" aria-live="polite">{t(p ? `account.err.password_${p}` : 'account.pw_ok')}</small>;
}

// ------------------------------------------------------------------------------------------- sign in
function SignIn({ t, origin, email, setEmail, onForgot, site, host }: { t: T; origin: string; email: string; setEmail: (v: string) => void; onForgot: () => void; site: string | null; host: string | null }) {
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [mfa, setMfa] = useState(false);
  const [choose, setChoose] = useState<{ workspaceId: string; workspaceName: string; role: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const totpRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (mfa) totpRef.current?.focus(); }, [mfa]);
  const submit = async (workspaceId?: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await accountService()!.signIn(origin, { email, password, ...(mfa && totp.trim() ? { totp: totp.trim() } : {}), ...(workspaceId ? { workspaceId } : {}) });
      if ('choose' in r) { setChoose(r.choose); setBusy(false); }
      // success: the account state changes and the app opens
    } catch (e) {
      if (e instanceof PlatformError && e.message === 'mfa_required') { setMfa(true); setErr(null); }
      else setErr(errorText(t, e));
      setBusy(false);
    }
  };
  // Oct 2026 (idea 40): sign in with a passkey — no e-mail or password typed; several workspaces → the same chooser
  const [passkeyOn, setPasskeyOn] = useState(false);
  const [pkChoice, setPkChoice] = useState<string | null>(null);
  useEffect(() => { if (passkeysSupported()) void accountService()?.info(origin).then((i) => setPasskeyOn(!!i.passkeys)).catch(() => undefined); }, [origin]);
  const withPasskey = async (workspaceId?: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await accountService()!.signInWithPasskey(origin, getPasskey, workspaceId && pkChoice ? { choice: pkChoice, workspaceId } : undefined);
      if ('choose' in r) { setChoose(r.choose); setPkChoice(r.choice ?? null); setBusy(false); }
    } catch (e) { setErr(e instanceof PlatformError ? errorText(t, e) : t('passkey.cancelled')); setBusy(false); }
  };
  const can = !!email.trim() && !!password && (!mfa || totp.trim().length >= 6);
  return (
    <form className="gate-stack" onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); if (can && !busy) void submit(); }} data-testid="gate-signin" noValidate>
      <Text t={t} id="g-email" label={t('common.email')} type="email" autoComplete="username" value={email} onChange={setEmail} testId="gate-email" />
      <PasswordField t={t} id="g-password" label={t('account.password')} value={password} onChange={setPassword} autoComplete="current-password" testId="gate-password"
        extra={<button type="button" className="linklike small" onClick={onForgot} data-testid="gate-forgot">{t('gate.forgot')}</button>} />
      {mfa && <div className="field gate-field" data-testid="gate-mfa">
        <label className="label" htmlFor="g-totp">{t('account.mfa_code')}</label>
        <input id="g-totp" ref={totpRef} className="input gate-code" inputMode="numeric" autoComplete="one-time-code" value={totp} onChange={(e: Ev) => setTotp(e.target.value)} data-testid="gate-totp" />
        <small className="tiny muted">{t('account.mfa_code_hint')}</small>
      </div>}
      {choose && <div className="gate-stack" data-testid="gate-choose" role="group" aria-label={t('account.choose_workspace')}>
        <span className="small muted">{t('account.choose_workspace')}</span>
        {choose.map((w) => <button key={w.workspaceId} type="button" className="btn" disabled={busy} onClick={() => void (pkChoice ? withPasskey(w.workspaceId) : submit(w.workspaceId))}>{w.workspaceName} · {t(`sync.role.${w.role}`)}</button>)}
      </div>}
      {err && <p className="gate-error" role="alert" data-testid="gate-error">{err}</p>}
      <button className="btn btn-primary gate-submit" type="submit" disabled={!can || busy} data-testid="gate-signin-submit">{t(busy ? 'account.working' : 'account.sign_in')}</button>
      {passkeyOn && <button type="button" className="btn gate-submit" disabled={busy} onClick={() => void withPasskey()} data-testid="gate-passkey">{t('passkey.sign_in')}</button>}
      {site && host && <>
        <div className="gate-or" aria-hidden="true"><span>{t('gate.or')}</span></div>
        <button type="button" className="btn gate-submit" onClick={() => accountService()!.startWebsiteSignIn()} data-testid="gate-website">{t('account.website.continue', { site: host })}</button>
      </>}
    </form>
  );
}

// ------------------------------------------------------------------------------------------- register
function Register({ t, lang, origin, info, invite }: { t: T; lang: Lang; origin: string; info: PlatformInfo | null; invite: string }) {
  const mode = info?.registration ?? 'open';
  const [code, setCode] = useState(invite);
  const [codeOpen, setCodeOpen] = useState(!!invite);
  const [unlocked, setUnlocked] = useState(!!invite);
  const [f, setF] = useState(() => ({ firstName: '', lastName: '', email: '', phone: '', password: '', password2: '', country: 'CY', company: '', licenceNumber: '', accept: false, marketing: false }));
  const set = (k: keyof typeof f) => (v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pack = regionPack(f.country);
  // joining an agency by invitation (the only way in while registration is closed) does not ask for a licence number
  const licenceRequired = mode !== 'closed' && pack.licensing.required.includes('licenceNumber');
  const needsCode = mode !== 'open';
  const missing = !f.firstName.trim() || !f.lastName.trim() || !f.email.trim() || !!passwordProblem(f.password, f.email) || f.password !== f.password2 || !f.accept
    || (licenceRequired && !f.licenceNumber.trim()) || (needsCode && !code.trim());
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await accountService()!.register(origin, {
        firstName: f.firstName.trim(), lastName: f.lastName.trim(), email: f.email, phone: f.phone.trim(), password: f.password,
        country: f.country, currency: pack.currency, language: lang, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
        company: f.company.trim(), licenceNumber: f.licenceNumber.trim(), workspaceName: f.company.trim() || null,
        activationCode: code.trim() || null, acceptTerms: f.accept, acceptPrivacy: f.accept, marketingConsent: f.marketing,
      });
    } catch (e) { setErr(errorText(t, e)); setBusy(false); }
  };

  // closed / code-only: first the code, then the form
  if (needsCode && !unlocked) return (
    <form className="gate-stack" onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); if (code.trim()) setUnlocked(true); }} data-testid="gate-register-code">
      <div className="gate-panel">
        <b>{t(mode === 'closed' ? 'gate.reg_closed_title' : 'gate.reg_code_title')}</b>
        <p className="small muted">{t(mode === 'closed' ? 'gate.reg_closed_body' : 'gate.reg_code_body')}</p>
      </div>
      <div className="field gate-field">
        <label className="label" htmlFor="g-code">{t(mode === 'closed' ? 'gate.invite_code' : 'gate.code_label')}</label>
        <input id="g-code" className="input gate-code" value={code} onChange={(e: Ev) => setCode(e.target.value)} autoComplete="off" spellCheck={false} autoCapitalize="characters" data-testid="gate-code" />
      </div>
      <button className="btn btn-primary gate-submit" type="submit" disabled={!code.trim()} data-testid="gate-code-continue">{t('common.next')}</button>
    </form>
  );

  const consent = t('gate.accept', { terms: '\u0001', privacy: '\u0002' }).split(/(\u0001|\u0002)/);
  return (
    <form className="gate-stack" onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); if (!missing && !busy) void submit(); }} data-testid="gate-register" noValidate>
      {mode === 'open' && info && <p className="small muted gate-lead">{t('account.trial_note', { days: info.trialDays })}</p>}
      {needsCode && <p className="small gate-lead" data-testid="gate-code-set">{t('gate.code_set', { code: code.trim() })} <button type="button" className="linklike" onClick={() => setUnlocked(false)}>{t('gate.change')}</button></p>}
      <div className="gate-grid">
        <Text t={t} id="r-first" label={t('account.first_name')} autoComplete="given-name" value={f.firstName} onChange={set('firstName')} testId="gate-reg-first" />
        <Text t={t} id="r-last" label={t('account.last_name')} autoComplete="family-name" value={f.lastName} onChange={set('lastName')} testId="gate-reg-last" />
      </div>
      <Text t={t} id="r-email" label={t('common.email')} type="email" autoComplete="email" value={f.email} onChange={set('email')} testId="gate-reg-email" />
      <PasswordField t={t} id="r-password" label={t('account.password')} value={f.password} onChange={set('password')} autoComplete="new-password" testId="gate-reg-password" />
      <PasswordHint t={t} pw={f.password} email={f.email} />
      <PasswordField t={t} id="r-password2" label={t('account.password_repeat')} value={f.password2} onChange={set('password2')} autoComplete="new-password" testId="gate-reg-password2" />
      {f.password2 && f.password2 !== f.password && <small className="tiny" style={{ color: 'var(--high)' }}>{t('account.pw_mismatch')}</small>}
      <div className="gate-grid">
        <div className="field gate-field">
          <label className="label" htmlFor="r-country">{t('settings.region')}</label>
          <select id="r-country" className="select" value={f.country} onChange={(e: SelEv) => set('country')(e.target.value)} data-testid="gate-reg-country">{Object.keys(REGIONS).map((r) => <option key={r} value={r}>{t(`region.${r}`)}</option>)}</select>
        </div>
        <Text t={t} id="r-phone" label={t('account.mobile')} type="tel" autoComplete="tel" value={f.phone} onChange={set('phone')} optional />
      </div>
      <div className="gate-grid">
        <Text t={t} id="r-company" label={t('settings.company')} autoComplete="organization" value={f.company} onChange={set('company')} optional />
        {(pack.licensing.shown.includes('licenceNumber') || licenceRequired) && <Text t={t} id="r-licence" label={t('branding.field.licenceNumber')} value={f.licenceNumber} onChange={set('licenceNumber')} testId="gate-reg-licence" optional={!licenceRequired} />}
      </div>
      {mode === 'open' && (codeOpen
        ? <Text t={t} id="r-code" label={t('gate.code_label')} value={code} onChange={setCode} testId="gate-reg-code" optional />
        : <button type="button" className="linklike small gate-left" onClick={() => setCodeOpen(true)}>{t('gate.have_code')}</button>)}
      <div className="gate-consent">
        <label className="check"><input type="checkbox" checked={f.accept} onChange={(e: Ev) => set('accept')(e.target.checked)} data-testid="gate-reg-accept" />
          <span>{consent.map((part, i) => part === '\u0001' ? <a key={i} href={legalUrl('terms', lang)} target="_blank" rel="noopener">{t('gate.terms_link')}</a>
            : part === '\u0002' ? <a key={i} href={legalUrl('privacy', lang)} target="_blank" rel="noopener">{t('gate.privacy_link')}</a> : part)}</span></label>
        <label className="check"><input type="checkbox" checked={f.marketing} onChange={(e: Ev) => set('marketing')(e.target.checked)} /><span>{t('account.accept_marketing')}</span></label>
      </div>
      {err && <p className="gate-error" role="alert" data-testid="gate-error">{err}</p>}
      <button className="btn btn-primary gate-submit" type="submit" disabled={missing || busy} data-testid="gate-reg-submit">{t(busy ? 'account.working' : 'account.create')}</button>
      <p className="tiny muted">{t('gate.reg_after')}</p>
    </form>
  );
}

// ------------------------------------------------------------------------------------------- reset
function Reset({ t, origin, token0, email, setEmail, onDone, onBack }: { t: T; origin: string; token0: string; email: string; setEmail: (v: string) => void; onDone: () => void; onBack: () => void }) {
  const [sent, setSent] = useState(!!token0);
  const [token, setToken] = useState(token0);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => { setBusy(true); setErr(null); try { await accountService()!.forgot(origin, email); setSent(true); } catch (e) { setErr(errorText(t, e)); } finally { setBusy(false); } };
  const reset = async () => { setBusy(true); setErr(null); try { await accountService()!.reset(origin, token, pw); setPw(''); onDone(); } catch (e) { setErr(errorText(t, e)); setBusy(false); } };
  return (
    <form className="gate-stack" onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); if (busy) return; if (!sent) { if (email.trim()) void send(); } else if (token.trim() && !passwordProblem(pw, email)) void reset(); }} data-testid="gate-reset" noValidate>
      {!sent ? <>
        <p className="small muted gate-lead">{t('gate.reset_intro')}</p>
        <Text t={t} id="x-email" label={t('common.email')} type="email" autoComplete="username" value={email} onChange={setEmail} testId="gate-reset-email" />
        {err && <p className="gate-error" role="alert">{err}</p>}
        <button className="btn btn-primary gate-submit" type="submit" disabled={busy || !email.trim()} data-testid="gate-reset-send">{t(busy ? 'account.working' : 'account.send_reset')}</button>
      </> : <>
        <p className="small muted gate-lead" data-testid="gate-reset-sent">{t(token0 ? 'gate.reset_from_link' : 'account.reset_sent')}</p>
        {!token0 && <div className="field gate-field">
          <label className="label" htmlFor="x-token">{t('account.reset_code')}</label>
          <input id="x-token" className="input gate-code" value={token} onChange={(e: Ev) => setToken(e.target.value)} autoComplete="one-time-code" spellCheck={false} data-testid="gate-reset-token" />
        </div>}
        <PasswordField t={t} id="x-password" label={t('account.new_password')} value={pw} onChange={setPw} autoComplete="new-password" testId="gate-reset-password" />
        <PasswordHint t={t} pw={pw} email={email} />
        {err && <p className="gate-error" role="alert">{err}</p>}
        <button className="btn btn-primary gate-submit" type="submit" disabled={busy || !token.trim() || !!passwordProblem(pw, email)} data-testid="gate-reset-submit">{t(busy ? 'account.working' : 'account.set_password')}</button>
      </>}
      <button type="button" className="linklike small gate-left" onClick={onBack}>{t('gate.back_signin')}</button>
    </form>
  );
}

// ------------------------------------------------------------------------------- after the first sign-in
/** the workspace on this device takes its language and market from the account; nothing else is assumed */
export async function completeHostedSetup(ws: Workspace, acct: AccountUiState | null, lang: Lang) {
  // right after sign-in the profile may still be on its way: ask for it once
  const me = acct?.profile ? null : await accountService()?.refresh().catch(() => null);
  const country = String(acct?.profile?.country ?? me?.profile?.country ?? '').toUpperCase();
  try {
    await ws.run((v, c) => saveSetting(v, 'locale', lang, c));
    if (country && REGIONS[country]) await ws.run((v, c) => saveSetting(v, 'region', country, c));
    await ws.run((v, c) => saveSetting(v, 'onboarded', true, c));
  } catch { /* read-only workspace (e.g. an expired plan): the app opens anyway and explains why */ }
  afterSignIn();
}
/** after the entry: links from e-mails (…#/account?verify=…) keep their destination; everything else starts at Today */
export function afterSignIn() {
  if (!/^#\/account\?(.*&)?verify=/.test(location.hash)) navigate('/now', { replace: true });
}

export function HostedSetup({ ws, acct, lang, onDone }: { ws: Workspace; acct: AccountUiState | null; lang: Lang; onDone: () => void }) {
  const t = useMemo(() => createI18n({ locale: lang }), [lang]);
  useEffect(() => { let on = true; void completeHostedSetup(ws, acct, lang).finally(() => { if (on) onDone(); }); return () => { on = false; }; }, []);
  return <div className="boot" role="status" data-testid="gate-preparing">{t.t('gate.preparing')}</div>;
}
